<template>
	<HeaderBlock title="Settings" />
	<CustomerLoadWait :customer="customer" />
	<transition>
		<div v-if="customer" class="settings-page">

			<!-- Top card: Account & Navigation -->
			<div class="settings-card">
				<!-- Employer -->
				<div class="settings-row">
					<div class="settings-row-left">
						<img v-if="employer?.logoUri" :src="employer.logoUri" alt="" class="employer-logo" />
						<div v-else class="employer-dot"></div>
						<span class="settings-row-label">Employer</span>
					</div>
					<span class="settings-row-value">{{ employer?.name || '' }}</span>
				</div>

				<div class="settings-divider"></div>

				<!-- Personal Information -->
				<div class="settings-row" @click="$router.push('/personalinformation')">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-account-outline</v-icon>
						<span class="settings-row-label">Personal Information</span>
					</div>
					<img :src="require('@/assets/arrow-right.png')" alt="" class="settings-arrow" />
				</div>

				<div class="settings-divider"></div>

				<!-- Notifications Settings -->
				<div class="settings-row" @click="$router.push('/notifications')">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-bell-outline</v-icon>
						<span class="settings-row-label">Notifications Settings</span>
					</div>
					<img :src="require('@/assets/arrow-right.png')" alt="" class="settings-arrow" />
				</div>

				<div class="settings-divider"></div>

				<!-- Spending Cycle Settings -->
				<div class="settings-row" @click="$router.push('/setupspendinganalysis')">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-refresh</v-icon>
						<span class="settings-row-label">Spending Cycle Settings</span>
					</div>
					<img :src="require('@/assets/arrow-right.png')" alt="" class="settings-arrow" />
				</div>

				<div class="settings-divider"></div>

				<!-- WhatsApp Section -->
				<div class="whatsapp-section">
					<div class="whatsapp-header" @click="openWhatsAppDrawer">
						<div class="whatsapp-icons">
							<img src="@/assets/EnableWhatapp.png" alt="WhatsApp" class="whatsapp-icon-img" />
						</div>
					</div>
					<div class="whatsapp-title">WhatsApp AI Assistant</div>
					<div class="whatsapp-description">Your own personal financial assistant that is always available and never judgmental.</div>
					<MGButton text="How it works" :outline="true" :arrow="true" :noCaps="true" :leftAlign="true" :small="true" @click="openWhatsAppDrawer" />
				</div>
			</div>

			<!-- Bottom card: Actions -->
			<div class="settings-card">
				<!-- Clear Cache -->
				<div class="settings-row" :class="{ 'settings-row-disabled': cacheCleared }" @click="!cacheCleared && clearCache()">
					<div class="settings-row-left">
						<img :src="require('@/assets/cache.png')" alt="" class="settings-icon-img" />
						<span class="settings-row-label">Clear Cache</span>
						<img v-if="cacheCleared" src="@/assets/check-tick.svg" alt="" class="cache-tick" />
					</div>
				</div>

				<div class="settings-divider"></div>

				<!-- Hide Figures -->
				<div class="settings-row">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-lightning-bolt</v-icon>
						<span class="settings-row-label">Hide Figures</span>
						<img :src="require('@/assets/info.png')" alt="Info" class="settings-info-icon" @click="$refs.randomiseDrawer.openDrawer()" />
					</div>
					<MGToggle v-model="randomiseFigures" />
				</div>

				<div class="settings-divider"></div>

				<!-- Logout -->
				<div class="settings-row" @click="handleLogout">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-logout</v-icon>
						<span class="settings-row-label">Logout</span>
					</div>
				</div>
			</div>

			<!-- Admin card (admin users only) -->
			<div v-if="isAdminUser" class="settings-card">
				<div class="settings-row" @click="$router.push('/admin')">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-account</v-icon>
						<span class="settings-row-label">Admin Dashboard</span>
					</div>
					<img :src="require('@/assets/arrow-right.png')" alt="" class="settings-arrow" />
				</div>
			</div>

			<!-- Hidden settings (admin or localStorage flag) -->
			<div v-if="showHiddenSettings" class="settings-card">
				<div class="settings-row">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-cached</v-icon>
						<span class="settings-row-label">Use Local Cache</span>
					</div>
					<MGToggle v-model="useLocalCache" />
				</div>

				<div class="settings-divider"></div>

				<div class="settings-row">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-cached</v-icon>
						<span class="settings-row-label">Use Cached Endpoints</span>
					</div>
					<MGToggle v-model="useCachedEndpoints" />
				</div>

				<div class="settings-divider"></div>

				<div class="settings-row">
					<div class="settings-row-left">
						<v-icon size="24" class="settings-icon">mdi-cached</v-icon>
						<span class="settings-row-label">Well-Known Logos Only</span>
					</div>
					<MGToggle v-model="wellKnownLogosOnly" />
				</div>
			</div>

			<!-- Footer -->
			<div class="settings-footer">
				<div class="settings-footer-links">
					<a href="https://www.moneyguided.com/#/terms" class="settings-footer-link">Terms & Conditions</a>
					<a href="https://www.moneyguided.com/#/policy" class="settings-footer-link">Privacy Policy</a>
				</div>
				<div class="settings-footer-version">
					Money Guided 2026 - Version {{ appVersion }} ({{ parseInt($cookies.get('st')) }}, {{ parseInt($cookies.get('sb')) }})
				</div>
			</div>

		</div>
	</transition>

	<WhatsAppEnableDrawer
		ref="whatsAppEnableDrawer"
		:phoneNumber="customer?.person?.mobilePhone || ''"
		@enable="handleEnableWhatsApp"
		@done="handleWhatsAppEnableDone"
	/>

	<!-- Hide Figures Info -->
	<DrawerPanel
		ref="randomiseDrawer"
		:close="true"
		:divider="false"
		:openHeight="'50%'"
		:blurBackground="true"
		bottom="-95px"
		closedHeight="0px"
		backgroundColor="transparent"
		openBackgroundColor="#fff"
	>
		<div class="hide-drawer-content">
			<v-icon size="24">mdi-lightning-bolt</v-icon>
			<div class="hide-drawer-title">Hide financial information</div>
			<div class="hide-drawer-description">Switch this on and we’ll hide your real numbers – handy if you’re showing the app to someone.</div>
			<MGButton text="Close" @click="$refs.randomiseDrawer.closeDrawer()" />
		</div>
	</DrawerPanel>

	<!-- Logout Confirmation -->
	<transition name="fade">
		<div v-if="showLogoutConfirm" class="logout-overlay" @click="showLogoutConfirm = false">
			<div class="logout-confirm" @click.stop>
				<div class="logout-confirm-option" @click="confirmLogout">Logout</div>
				<div class="logout-confirm-divider"></div>
				<div class="logout-confirm-option logout-confirm-cancel" @click="showLogoutConfirm = false">Cancel</div>
			</div>
		</div>
	</transition>
</template>

<script>
import Services from '../mixins/Services';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import DrawerPanel from './elements/DrawerPanel.vue';
import MGToggle from './elements/MGToggle.vue';
import WhatsAppEnableDrawer from './elements/WhatsAppEnableDrawer.vue';
import { checkIsAdmin } from '../utils/authUtils';

export default {
	name: 'AppSettingsPage',
	mixins: [Services],
	components: { CustomerLoadWait, DrawerPanel, HeaderBlock, MGButton, MGToggle, WhatsAppEnableDrawer },
	data() {
		return {
			showLogoutConfirm: false,
			cacheCleared: false,
			randomiseFigures: localStorage.getItem('randomiseFigures') === 'true',
			showHiddenSettings: localStorage.getItem('showHiddenSettings') === 'true' || checkIsAdmin(this.$cookies),
			useLocalCache: localStorage.getItem('useLocalCache') === 'true',
			useCachedEndpoints: localStorage.getItem('useCachedEndpoints') === 'true',
			wellKnownLogosOnly: localStorage.getItem('wellKnownLogosOnly') !== null
				? localStorage.getItem('wellKnownLogosOnly') === 'true'
				: this.$wellKnownLogosOnly,
		}
	},
	computed: {
		isAdminUser() {
			return checkIsAdmin(this.$cookies);
		}
	},
	methods: {
		getCustomer() {
			this._getCustomer(() => {
				if (this.customer.employerID) {
					this._getEmployer(this.customer.employerID);
				}
			});
		},
		clearCache() {
			this._clearHttpCache();
			this.cacheCleared = true;
		},
		openWhatsAppDrawer() {
			if (this.$refs.whatsAppEnableDrawer) {
				this.$refs.whatsAppEnableDrawer.openDrawer();
			}
		},
		handleEnableWhatsApp() {
			this._enableWhatsApp();
		},
		handleWhatsAppEnableDone() {
			this._getCustomer(null, false);
		},
		handleLogout() {
			this.showLogoutConfirm = true;
		},
		confirmLogout() {
			window.location.href = 'MG_POP.html?view=logout';
		},
		onLoad() {
			this.getCustomer();
		}
	},
	mounted() {
		this.onLoad();
	},
	watch: {
		randomiseFigures(val) {
			localStorage.setItem('randomiseFigures', val);
		},
		useLocalCache(val) {
			localStorage.setItem('useLocalCache', val);
		},
		useCachedEndpoints(val) {
			localStorage.setItem('useCachedEndpoints', val);
		},
		wellKnownLogosOnly(val) {
			localStorage.setItem('wellKnownLogosOnly', val);
		},
	}
}
</script>

<style scoped>
.settings-page {
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding-bottom: 32px;
}

.settings-card {
	background: var(--card-bg-color, #fff);
	border-radius: 12px;
	padding: 16px 20px;
}

.settings-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 4px 0;
	gap: 20px;
}

.settings-row-left {
	display: flex;
	align-items: center;
	gap: 20px;
}

.settings-icon {
	color: var(--text-color);
	opacity: 0.7;
}

.settings-icon-img {
	width: 24px;
	height: 24px;
}

.settings-row-label {
	font-size: 15px;
	color: var(--text-color);
}

.settings-row-value {
	font-size: 15px;
	color: var(--text-color);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.settings-arrow {
	width: 16px;
	height: 10px;
}

.settings-divider {
	height: 1px;
	background: var(--text-color);
	opacity: 0.1;
	margin: 8px 0;
}

.employer-logo {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
}

.employer-dot {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: var(--text-color);
	opacity: 0.2;
}

.settings-row-right {
	display: flex;
	align-items: center;
	gap: 8px;
}

.settings-info-icon {
	width: 24px;
	height: 24px;
}


.cache-tick {
	width: 15px;
	height: 15px;
}

.settings-row-disabled {
	opacity: 0.5;
	pointer-events: none;
}

/* WhatsApp section */
.whatsapp-section {
	padding: 24px 0 4px;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.whatsapp-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.whatsapp-icons {
	display: flex;
	align-items: center;
	gap: 0;
}

.whatsapp-icon-img {
	width: 73px;
	height: auto;
}

.whatsapp-title {
	font-size: 16px;
	font-weight: bold;
	color: var(--text-color);
}

.whatsapp-description {
	font-size: 13px;
	margin-bottom: -20px;
}

.whatsapp-settings-btn {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border: 1px solid var(--text-color);
	border-radius: 100px;
	padding: 8px 16px;
}

.whatsapp-settings-text {
	font-size: 13px;
	font-weight: bold;
	color: var(--text-color);
}

/* Footer */
.settings-footer {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.settings-footer-links {
	display: flex;
	gap: 13px;
}

.settings-footer-link {
	font-size: 13px;
	line-height: 1.23;
	color: var(--text-color);
	text-decoration: none;
}

.settings-footer-version {
	font-size: 13px;
	line-height: 1.23;
	color: var(--text-color);
	opacity: 0.6;
}

/* Logout confirmation */
.logout-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.4);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.logout-confirm {
	width: 284px;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0px 0px 15px 0px rgba(51, 40, 71, 0.15);
	overflow: hidden;
}

.logout-confirm-option {
	padding: 13px 30px;
	font-size: 15px;
	line-height: 1.47;
	color: #0F6CE8;
	text-align: center;
}

.logout-confirm-cancel {
	font-weight: bold;
}

.logout-confirm-divider {
	height: 1px;
	background: #000;
	opacity: 0.1;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Hide figures drawer */
.hide-drawer-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	text-align: center;
}

.hide-drawer-title {
	font-size: 18px;
	font-weight: bold;
	color: var(--text-color);
}

.hide-drawer-description {
	font-size: 13px;
	color: var(--text-color);
}

.randomise-drawer-title {
	font-size: 18px;
	font-weight: bold;
	color: var(--text-color);
}

.randomise-drawer-description {
	font-size: 13px;
	line-height: 1.23;
	color: var(--text-color);
}
</style>
